import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowRight, Home, ShieldCheck, CheckCircle2 } from 'lucide-react';
import authService from '../../services/authService';
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const LogoImg = '/logo.png';
    const [isLogin, setIsLogin] = useState(location.pathname !== '/register');
    
    const [formData, setFormData] = useState({
        username: '', password: '', confirmPassword: '', fullName: '', phone: '', email: ''
    });

    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);

    // Reset data khi chuyển tab đăng nhập / đăng ký
    useEffect(() => {
        setIsLogin(location.pathname !== '/register');
        setFormData({username: '', password: '', confirmPassword: '', fullName: '', phone: '', email: ''});
        setOtpSent(false);
        setOtp('');
        setCountdown(0);
    }, [location.pathname]);

    // Xử lý đếm ngược OTP
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Hàm kiểm tra form hợp lệ để mở khóa nút Submit
    const isFormValid = () => {
        if (isLogin) {
            return formData.username.trim() !== '' && formData.password.trim() !== '';
        } else {
            return (
                formData.fullName.trim() !== '' &&
                formData.phone.trim() !== '' &&
                formData.email.trim() !== '' &&
                formData.username.trim() !== '' &&
                formData.password.length >= 8 &&
                formData.password === formData.confirmPassword &&
                otp.length === 6
            );
        }
    };

    const handleSendEmailOtp = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email || !emailRegex.test(formData.email)) {
            toast.warn('Vui lòng nhập địa chỉ email hợp lệ!');
            return;
        }

        setOtpLoading(true);
        try {
            await authService.sendEmailOtp(formData.email);
            setOtpSent(true);
            setCountdown(60);
            toast.success('Mã OTP đã được gửi đến Email của bạn!');
        } catch (error) {
            // Hiển thị trực tiếp lỗi từ server (nếu email đã tồn tại)
            const errorMessage = error.response?.data?.message || error.response?.data || 'Lỗi hệ thống!';
            toast.error(typeof errorMessage === 'string' ? errorMessage : 'Không thể gửi OTP lúc này!');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        try {
            const response = await authService.googleLogin(credentialResponse.credential);
            const accountDto = response;
            
            localStorage.setItem('access_token', accountDto.token);
            localStorage.setItem('user_info', JSON.stringify({
                accountId: accountDto.accountId || accountDto.id || accountDto.userId,
                username: accountDto.username,
                role: accountDto.role,
                accountName: accountDto.accountName
            }));
            window.dispatchEvent(new Event('authChange'));
            
            toast.success('Đăng nhập bằng Google thành công! ✨');
            navigate('/');
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Có lỗi xảy ra khi xác thực Google!';
            toast.error(typeof errorMessage === 'string' ? errorMessage : 'Đăng nhập thất bại!');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Không cho submit nếu form chưa thỏa mãn
        if (!isFormValid()) return;

        setLoading(true);
        try {
            if (isLogin) {
                const response = await authService.login(formData.username, formData.password);
                localStorage.setItem('access_token', response.token);
                localStorage.setItem('user_info', JSON.stringify({
                    accountId: response.accountId || response.id || response.userId,
                    username: response.username,
                    role: response.role,
                    accountName: response.accountName
                }));
                window.dispatchEvent(new Event('authChange'));
                navigate('/');
            } else {
                await authService.register({
                    username: formData.username,
                    password: formData.password,
                    accountName: formData.fullName, 
                    phone: formData.phone,
                    email: formData.email,
                    role: 'ROLE_CUSTOMER',
                    otp: otp 
                });
                toast.success('Đăng ký thành công! Vui lòng đăng nhập. ✨');
                setIsLogin(true); 
                setFormData(prev => ({ ...prev, password: '', confirmPassword: '' })); 
            }
        } catch (err) {
            // Hiển thị lỗi từ backend (Ví dụ: "Username already exists")
            const errorMessage = err.response?.data?.message || err.response?.data || 'Có lỗi xảy ra, vui lòng thử lại!';
            toast.error(typeof errorMessage === 'string' ? errorMessage : 'Lỗi hệ thống!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white flex flex-col justify-center items-center p-4 relative">
            <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-tnc-orange transition-colors font-medium">
                <Home size={20} /> Quay lại trang chủ
            </Link>

            <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] overflow-hidden border border-gray-100 my-8">
                <div className="bg-white p-6 pb-0 text-center relative overflow-hidden flex flex-col items-center border-b border-gray-50">
                    <div className="bg-white p-6 text-center relative overflow-hidden flex flex-col items-center border-b border-gray-50">
                        {/* Thanh highlight mảnh mai phía trên */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-tnc-orange/80"></div>
                        
                        {/* Logo thu nhỏ lại một chút cho cân đối */}
                        <div className="mb-3">
                            <img src={LogoImg} alt="Logo" className="h-14 w-auto object-contain" />
                        </div>

                        {/* Khối tiêu đề chính */}
                        <div className="py-2">
                            <h2 className="text-xl md:text-2xl font-black text-tnc-orange tracking-tight uppercase">
                                {isLogin ? 'Đăng Nhập' : 'Tạo Tài Khoản'}
                            </h2>
                            <div className="mt-2 w-8 h-0.5 bg-tnc-orange mx-auto rounded-full opacity-30"></div>
                        </div>
                    </div>
                </div>

                <div className="p-8 pt-0">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!isLogin && (
                            <>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <User size={18} />
                                    </div>
                                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required={!isLogin}
                                        className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm transition-colors bg-gray-50 focus:bg-white"
                                        placeholder="Họ và tên *"
                                    />
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <Phone size={18} />
                                    </div>
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required={!isLogin}
                                        className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm transition-colors bg-gray-50 focus:bg-white"
                                        placeholder="Số điện thoại *"
                                    />
                                </div>

                                {/* Ô NHẬP EMAIL CÓ KÈM NÚT GỬI MÃ OTP */}
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <Mail size={18} />
                                    </div>
                                    <input 
                                        type="email" name="email" value={formData.email} onChange={handleChange} 
                                        required={!isLogin} disabled={otpSent} 
                                        className="w-full pl-10 pr-[140px] py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm transition-colors bg-gray-50 focus:bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                        placeholder="Địa chỉ Email *"
                                    />
                                    
                                    <button 
                                        type="button" onClick={handleSendEmailOtp}
                                        disabled={otpLoading || !formData.email || countdown > 0}
                                        className={`absolute right-1.5 top-1.5 bottom-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center min-w-[125px]
                                            ${otpSent && countdown === 0 ? 'bg-orange-100 text-tnc-orange hover:bg-orange-200' : ''}
                                            ${!otpSent && !countdown ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : ''}
                                            ${countdown > 0 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        {otpLoading ? (
                                            <span className="flex items-center gap-1"><span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full"></span> ĐANG GỬI...</span>
                                        ) : countdown > 0 ? (
                                            `GỬI LẠI SAU ${countdown}s`
                                        ) : (
                                            'GỬI MÃ OTP'
                                        )}
                                    </button>
                                </div>

                                {/* KHUNG OTP (Chỉ hiện ra khi đã bấm gửi mã) */}
                                {otpSent && (
                                    <div className="relative animate-fade-in-down">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <ShieldCheck size={18} className="text-green-500" />
                                        </div>
                                        <input 
                                            type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} 
                                            maxLength={6} 
                                            className="w-full pl-10 pr-10 py-3 border border-green-300 rounded-xl focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-sm font-semibold tracking-widest text-center transition-colors bg-green-50 text-green-700 placeholder:text-green-400 placeholder:tracking-normal placeholder:font-normal"
                                            placeholder="Nhập 6 số OTP từ Email *"
                                        />
                                        {otp.length === 6 && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none animate-bounce">
                                                <CheckCircle2 size={18} className="text-green-500" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                <User size={18} />
                            </div>
                            <input type="text" name="username" value={formData.username} onChange={handleChange} required
                                className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm transition-colors bg-gray-50 focus:bg-white"
                                placeholder={isLogin ? "Tên đăng nhập hoặc Email *" : "Tên đăng nhập (Username) *"}
                            />
                        </div>

                        {/* MẬT KHẨU */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                    <Lock size={18} />
                                </div>
                                <input type="password" name="password" value={formData.password} onChange={handleChange} required
                                    className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none text-sm transition-colors bg-gray-50 focus:bg-white ${
                                        !isLogin && formData.password && formData.password.length < 8
                                        ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500' 
                                        : 'border-gray-200 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange'
                                    }`}
                                    placeholder="Mật khẩu *"
                                />
                            </div>
                            {/* Báo lỗi trực tiếp dưới ô password */}
                            {!isLogin && formData.password && formData.password.length < 8 && (
                                <p className="text-xs text-red-500 pl-2 mt-1.5 font-medium">Mật khẩu phải có ít nhất 8 ký tự</p>
                            )}
                        </div>

                        {/* XÁC NHẬN MẬT KHẨU */}
                        {!isLogin && (
                            <div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <Lock size={18} />
                                    </div>
                                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required={!isLogin}
                                        className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none text-sm transition-colors bg-gray-50 focus:bg-white ${
                                            formData.confirmPassword && formData.password !== formData.confirmPassword
                                            ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                            : 'border-gray-200 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange'
                                        }`}
                                        placeholder="Xác nhận mật khẩu *"
                                    />
                                </div>
                                {/* Báo lỗi trực tiếp dưới ô confirm password */}
                                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                    <p className="text-xs text-red-500 pl-2 mt-1.5 font-medium">Mật khẩu xác nhận không khớp</p>
                                )}
                            </div>
                        )}

                        {isLogin && (
                            <div className="flex justify-end pt-1">
                                <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-tnc-orange font-medium transition-colors">
                                    Quên mật khẩu?
                                </Link>
                            </div>
                        )}

                        {/* NÚT SUBMIT */}
                        <button type="submit" disabled={loading || !isFormValid()}
                            className={`w-full font-bold py-3.5 rounded-xl transition-all flex justify-center items-center gap-2 mt-6 active:scale-[0.98] ${
                                !isFormValid() 
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                                : 'bg-tnc-orange hover:bg-[#d65500] text-white shadow-lg shadow-orange-500/30'
                            }`}
                        >
                            {loading ? (
                                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                            ) : (
                                <>
                                    {isLogin ? 'ĐĂNG NHẬP' : 'HOÀN TẤT ĐĂNG KÝ'}
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-6">
                        <div className="relative flex items-center py-2">
                            <div className="flex-grow border-t border-gray-200"></div>
                            <span className="flex-shrink-0 mx-4 text-gray-400 text-sm font-medium">
                                Hoặc {isLogin ? 'đăng nhập' : 'đăng ký'} nhanh qua
                            </span>
                            <div className="flex-grow border-t border-gray-200"></div>
                        </div>
                        
                        <div className="flex justify-center mt-5 w-full">
                            <div className="transform scale-[1.08] hover:scale-[1.1] origin-center transition-all duration-300 rounded-full">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => toast.error('Đăng nhập Google thất bại.')}
                                    useOneTap={isLogin} 
                                    theme="outline"            
                                    size="large"               
                                    text={isLogin ? 'signin_with' : 'signup_with'} 
                                    shape="pill"               
                                    width="320" 
                                    logo_alignment="center"    
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 text-center text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                        {isLogin ? "Chưa có tài khoản?" : "Đã có tài khoản?"}
                        <Link 
                            to={isLogin ? '/register' : '/login'}
                            className="ml-2 font-bold text-tnc-orange hover:underline transition-colors"
                        >
                            {isLogin ? 'Đăng ký ngay' : 'Đăng nhập tại đây'}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;